/**
 * Read a design token's computed value.
 *
 * Chart.js draws to a canvas, so it needs a real colour string — `var(--teal)`
 * in a dataset's `borderColor` paints nothing. Everything else in the app can
 * hand the token straight to CSS or to an SVG attribute; this is the one place
 * that has to resolve it, and it resolves it from the stylesheet rather than
 * restating the hex, so a token change reaches the canvas too.
 */
// Keyed by theme, not by token name alone. Every entry is only true for the
// theme that was on the root when it was read, and the cache cannot see the
// attribute change that invalidates it — so the theme goes in the key and a
// stale entry becomes unreachable rather than wrong. `clearTokenCache()` is
// belt and braces on top of this, not the only thing standing between a canvas
// and the previous palette.
const cache = new Map();

const themeKey = () => document.documentElement.getAttribute('data-theme') || 'dark';

export const token = (name) => {
    if (typeof document === 'undefined') return '';
    const key = `${themeKey()}|${name}`;
    if (cache.has(key)) return cache.get(key);
    const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    cache.set(key, value);
    return value;
};

/** Forget the resolved values — a theme swap redefines every one of them. */
export const clearTokenCache = () => cache.clear();

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;
const RGB = /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+[\d.%]+)?\s*\)$/i;

/**
 * The token's own colour at a given opacity — what a band fill needs.
 *
 * Both colour forms the stylesheet uses, because not every token is a hex:
 * `--line`, `--grid` and the rest of the hairlines are already `rgba(...)`, and
 * returning those unchanged would hand back an *opaque* band fill rather than
 * an error — a miss you find by eye months later. Anything that is neither form
 * (`transparent`, `none`, an unset token) comes back as it went in, which is
 * the only sane thing to do with it.
 */
export const tokenAlpha = (name, alpha) => {
    const value = token(name);
    if (HEX.test(value)) {
        const hex = value.slice(1);
        const full = hex.length === 3 ? hex.split('').map(c => c + c).join('') : hex;
        const n = parseInt(full, 16);
        return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
    }
    const rgb = RGB.exec(value);
    if (rgb) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${alpha})`;
    return value;
};
